/**
 * Stream B — B78-B83: Invoice, Tax, Credit Note, Payment Plan, Dunning
 *
 * Full billing schema for invoice lifecycle:
 * invoices → tax_rates → credit_notes → payment_plans → dunning_log
 */
import {
  pgTable, uuid, text, integer, timestamp, jsonb, boolean,
  index, uniqueIndex, numeric,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export const INVOICE_STATUSES = ["draft", "open", "paid", "void", "uncollectible", "overdue"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const invoices = pgTable(
  "invoices",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /** Sequential invoice number per tenant: INV-2026-0001 */
    invoiceNumber: text("invoice_number").notNull(),
    stripeInvoiceId: text("stripe_invoice_id"),
    /** Customer info (denormalized for PDF generation) */
    customerName: text("customer_name").notNull(),
    customerEmail: text("customer_email").notNull(),
    customerAddress: jsonb("customer_address"),
    customerVatId: text("customer_vat_id"),
    customerCountryCode: text("customer_country_code"),
    /** Amounts in cents */
    subtotalCents: integer("subtotal_cents").notNull(),
    taxAmountCents: integer("tax_amount_cents").default(0).notNull(),
    totalCents: integer("total_cents").notNull(),
    amountPaidCents: integer("amount_paid_cents").default(0).notNull(),
    amountDueCents: integer("amount_due_cents").notNull(),
    currencyCode: text("currency_code").default("eur").notNull(),
    /** Tax breakdown per line */
    taxBreakdown: jsonb("tax_breakdown").$type<Array<{ countryCode: string; rate: number; amountCents: number }>>(),
    /** Line items */
    lineItems: jsonb("line_items").$type<Array<{
      description: string;
      quantity: number;
      unitPriceCents: number;
      totalCents: number;
      taxRate: number;
    }>>().notNull(),
    status: text("status").default("draft").notNull(),
    /** Dates */
    issuedAt: timestamp("issued_at", tz),
    dueAt: timestamp("due_at", tz),
    paidAt: timestamp("paid_at", tz),
    voidedAt: timestamp("voided_at", tz),
    voidReason: text("void_reason"),
    /** PDF stored in R2 */
    pdfUrl: text("pdf_url"),
    pdfGeneratedAt: timestamp("pdf_generated_at", tz),
    /** Stripe hosted invoice URL for payment */
    hostedInvoiceUrl: text("hosted_invoice_url"),
    metadata: jsonb("metadata").default({}).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_invoice_number").on(table.tenantId, table.invoiceNumber),
    uniqueIndex("uq_invoice_stripe").on(table.stripeInvoiceId).where(sql`stripe_invoice_id IS NOT NULL`),
    index("idx_invoice_tenant_status").on(table.tenantId, table.status),
    index("idx_invoice_tenant_date").on(table.tenantId, table.issuedAt),
  ],
);

// ---------------------------------------------------------------------------
// Tax Rates
// ---------------------------------------------------------------------------

export const taxRates = pgTable(
  "tax_rates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    countryCode: text("country_code").notNull(),
    countryName: text("country_name").notNull(),
    regionCode: text("region_code"),
    rate: numeric("rate", { precision: 5, scale: 2 }).notNull(), // e.g. 19.00
    rateType: text("rate_type").default("standard").notNull(), // standard, reduced, zero
    stripeTaxRateId: text("stripe_tax_rate_id"),
    isDefault: boolean("is_default").default(false).notNull(),
    effectiveFrom: timestamp("effective_from", tz).defaultNow().notNull(),
    effectiveTo: timestamp("effective_to", tz), // null = current
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    index("idx_tax_rates_country").on(table.countryCode, table.isActive),
    index("idx_tax_rates_country_region").on(table.isActive, table.countryCode, table.regionCode),
  ],
);

// ---------------------------------------------------------------------------
// Credit Notes
// ---------------------------------------------------------------------------

export const creditNotes = pgTable(
  "credit_notes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    invoiceId: uuid("invoice_id").notNull(),
    creditNoteNumber: text("credit_note_number").notNull(),
    stripeCreditNoteId: text("stripe_credit_note_id"),
    amountCents: integer("amount_cents").notNull(),
    reason: text("reason").notNull(),
    status: text("status").default("issued").notNull(), // issued, void
    issuedBy: text("issued_by").notNull(), // userId
    issuedAt: timestamp("issued_at", tz).defaultNow().notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_credit_note_number").on(table.tenantId, table.creditNoteNumber),
    index("idx_credit_note_invoice").on(table.invoiceId),
    index("idx_credit_note_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// Payment Plans
// ---------------------------------------------------------------------------

export const PLAN_STATUSES = ["active", "completed", "cancelled", "defaulted"] as const;
export type PaymentPlanStatus = (typeof PLAN_STATUSES)[number];

export const paymentPlans = pgTable(
  "payment_plans",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    invoiceId: uuid("invoice_id").notNull(),
    totalAmountCents: integer("total_amount_cents").notNull(),
    installmentCount: integer("installment_count").notNull(),
    status: text("status").default("active").notNull(),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    completedAt: timestamp("completed_at", tz),
  },
  (table) => [
    index("idx_payment_plan_tenant").on(table.tenantId),
    index("idx_payment_plan_invoice").on(table.invoiceId),
  ],
);

export const INSTALLMENT_STATUSES = ["pending", "paid", "failed", "cancelled"] as const;

export const paymentPlanInstallments = pgTable(
  "payment_plan_installments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    planId: uuid("plan_id").notNull(),
    tenantId: uuid("tenant_id").notNull(),
    installmentNumber: integer("installment_number").notNull(),
    amountCents: integer("amount_cents").notNull(),
    dueAt: timestamp("due_at", tz).notNull(),
    status: text("status").default("pending").notNull(),
    stripePaymentIntentId: text("stripe_payment_intent_id"),
    paidAt: timestamp("paid_at", tz),
    failedAt: timestamp("failed_at", tz),
    failureReason: text("failure_reason"),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
  },
  (table) => [
    index("idx_installment_plan").on(table.planId),
    index("idx_installment_due").on(table.tenantId, table.dueAt, table.status),
  ],
);

// ---------------------------------------------------------------------------
// Dunning Log
// ---------------------------------------------------------------------------

export const DUNNING_STAGES = ["day1_email", "day3_sms", "day7_final", "day10_suspend"] as const;
export type DunningStage = (typeof DUNNING_STAGES)[number];

export const dunningLog = pgTable(
  "dunning_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    subscriptionId: text("subscription_id"),
    stage: text("stage").notNull(),
    channel: text("channel").notNull(), // email, sms
    sentAt: timestamp("sent_at", tz).defaultNow().notNull(),
    recoveredAt: timestamp("recovered_at", tz), // null until payment recovered
    metadata: jsonb("metadata").default({}).notNull(),
  },
  (table) => [
    index("idx_dunning_tenant").on(table.tenantId),
    index("idx_dunning_subscription").on(table.subscriptionId),
  ],
);
